// The ```netwatch-findings block: what the JSON means and what the component
// draws — no React, so a test runner can import it without jsdom (same split
// as chartSpec/NetwatchChart).
//
// The block is built end-to-end by the backend collector
// (app/graphs/tools/citrix_health.py, docs/runbook-findings-block.md): every
// sentence, verdict and number in it is a code template. This module only
// validates the shape tolerantly, derives presentation (verdict icon level,
// day-card kinds, sparkline paths) and refuses to draw anything it can't read.

import type { ChartBlock } from './chartSpec'
import type { SeverityKey } from './severity'

/** `critical` (docs/findings-everywhere.md) is the red Disaster octagon — a
 *  host offline now, a drive full within days, hosts below SLA. Older
 *  bundles paint it as the green tick (unknown key → clean). */
export type FindingsVerdictKey = 'clean' | 'moved' | 'no_spike' | 'chronic' | 'critical'
export type DayOutcome = 'clean' | 'spike' | 'no_data'

export interface FindingsDay {
  start: number
  end: number
  label: string
  outcome: DayOutcome
  peak: number | null
  spike_at: number | null
  cleared_at: number | null
  /** [t, y] pairs, only for spike days that still had raw history. */
  points: [number, number][] | null
  spike_at_precision?: 'hour'
  /** `17:58` / `~17:00` formatted server-side — the fallback when the block
   *  carries no epoch; the viewer's clock (from `spike_at`) wins otherwise. */
  spike_label?: string
  /** Backend-composed caption override ("high all hour" on a chronic strip) —
   *  wins over the derived "spike HH:MM". */
  caption?: string
}

export interface FindingsPattern {
  title: string
  subtitle: string
  summary: string
  tone: 'neutral' | 'warn'
  unit: string
  threshold: number | null
  days: FindingsDay[]
}

export interface FindingsMover {
  check: string
  label: string
  kind: 'deviating' | 'in_band'
  severity: 'warn' | 'strong' | null
  outcome: string
  correlation: number | null
  times_baseline: number | null
  detail: string
  value_text: string
  baseline_text: string | null
  chart: ChartBlock | null
}

export interface FindingsCheck {
  label: string
  outcome: string
  value_text: string
  /** Still emitted by the builder (and present on stored blocks) but no
   *  longer rendered on the metrics card — the table shows the metric and
   *  its min–max only. */
  baseline_text: string | null
  times_baseline: number | null
  correlation: number | null
  /** `min – max` across the spike windows (today's plus each earlier spike
   *  day in the strip) — the hours the pattern section is about, not today's
   *  average. Absent on blocks built before ranges existed. */
  range_text?: string | null
  /** How many of those windows had raw history for this metric. */
  windows?: number
}

export interface FindingsGroup {
  key: string
  label: string
  count: number
  /** `normal`, or `elevated` when a member sits over its threshold at the
   *  host's own usual level (kept out of the evidence by the baseline rule,
   *  but not "within range"). */
  outcome: string
  checks: FindingsCheck[]
}

/** Pill for a group / check outcome. */
export function groupPill(outcome: string): { cls: 'normal' | 'warn' | 'muted'; word: string } {
  if (outcome === 'normal') return { cls: 'normal', word: 'normal' }
  if (outcome === 'elevated') return { cls: 'warn', word: 'high, usual' }
  return { cls: 'muted', word: outcome }
}

/** One deterministic hypothesis check (the alert investigation's slate):
 *  what was tested and whether the evidence supports or refutes it. */
export interface FindingsSlateItem {
  check: string
  hypothesis: string
  outcome: 'supports' | 'refutes' | 'unknown' | string
  detail: string
  /** The outcome-RESOLVED sentence ("Not a fleet-wide issue — isolated to
   *  this host"), composed backend-side where the outcome is known. The row
   *  renders this as its primary line — a beta tester read the bare
   *  hypothesis as a finding, which the small "ruled out" pill couldn't
   *  undo. Absent on legacy rows → the hypothesis renders as before. */
  finding?: string
}

export interface FindingsSlate {
  title: string
  summary?: string
  items: FindingsSlateItem[]
}

/** One process that recurred across the spike windows (docs/process-recurrence.md):
 *  its min–max over the samples it was listed in, and how many of the
 *  windows it appeared in. Backend-composed; `text` is the same row as a
 *  line for the `lines` fallback. */
export interface FindingsProcessRow {
  name: string
  range_text: string
  windows: number
  windows_total: number
  /** Share of samples it was listed in — the single-window basis. */
  presence?: number
  unit?: string
  text?: string
}

/** The processes section, normalised: rows when the producer measured them,
 *  else the collector's snapshot lines (older blocks, alert strategies that
 *  never measured). */
export interface FindingsProcesses {
  title: string
  lines: string[]
  rows: FindingsProcessRow[]
  /** The snapshot nearest the peak. `at` is the epoch (viewer's clock wins
   *  over the server-stamped `label`, like the strip). */
  peak: { at: number | null; label: string; text: string } | null
  note: string | null
  basis: string | null
  windowsTotal: number
}

/** The diagnosis' own confidence, distinct from the host-shape verdict. */
export type FindingsVerdictState = 'confirmed' | 'probable' | 'inconclusive'

/** One titled grid inside the card (docs/findings-everywhere.md): the
 *  measured rows of a producer without a lead metric — outages, hosts below
 *  SLA, drives filling, lockouts by caller. Cells are strings the backend
 *  already formatted (units, timezone); the component never formats. */
export interface FindingsTableColumn {
  key: string
  label: string
  align?: 'left' | 'right'
}
export type FindingsRowTone = 'warn' | 'strong' | 'muted'
export interface FindingsTableRow {
  /** Colours the leading cell (warn amber, strong red) or greys the row (muted). */
  tone?: FindingsRowTone
  /** A sub-row under the previous one (lockout sources under an account). */
  indent?: boolean
  [cell: string]: string | boolean | undefined
}
export interface FindingsTable {
  title: string
  columns: FindingsTableColumn[]
  rows: FindingsTableRow[]
  /** Code-composed line under the grid ("2 more not shown"). */
  note?: string | null
  /** Shown instead of the grid when there are no rows. */
  empty?: string | null
}

/** The JSON inside a ```netwatch-findings fence, once hydrated. */
export interface FindingsBlock {
  kind?: string
  version?: number
  ref?: string
  /** `caption` is one backend-composed line under the verdict ("CPU
   *  utilisation on app-01, 1h to 14:57"); the component never composes prose. */
  subject?: { type?: string; name?: string; host?: string; caption?: string }
  window?: { from: number; till: number; minutes: number }
  verdict?: { key: FindingsVerdictKey | string; title: string; detail: string; state?: FindingsVerdictState | string | null }
  checked?: number
  lead?: { check: string; label: string; unit: string; spiked: boolean; detail?: string } | null
  pattern?: FindingsPattern | null
  /** null = this producer never measured movers (a checks-only alert
   *  strategy) — the section is omitted, not titled "Nothing moved with it". */
  movers?: {
    title: string
    summary: string
    strongest_correlation: number | null
    link_threshold: number
    items: FindingsMover[]
  } | null
  normal?: {
    title: string
    summary: string
    groups: FindingsGroup[]
    /** The spike windows the groups were measured over, when there was more
     *  than today's (the title then says "measured during the spikes"). */
    windows?: Array<{ from: number; till: number; label: string }>
    gaps: { count: number; labels: string[] }
  } | null
  slate?: FindingsSlate | null
  look?: { title: string; text: string } | null
  /** `title` arms the in-card render (alert producer, amendment 20) — an
   *  untitled `next` (runbook/spike vocabulary asks) stays machine-facing. */
  next?: { title?: string; ask_now: string[]; needs_collection: string[] }
  /** Amendment 20 (alert producer): the collector's kind-tagged evidence
   *  sections and the host's other open problems, lifted into the card so
   *  the whole investigation renders as one card. Lines are markdown-ish
   *  bullets ("- …", nested "  - …", `**bold**`, backtick code). */
  recurring?: { title: string; lines: string[] } | null
  /** `rows`/`peak` arrive from producers that measured processes across the
   *  spike windows; `lines` alone is the older shape (still rendered). */
  processes?: {
    title: string
    lines?: string[]
    rows?: FindingsProcessRow[]
    peak?: { at?: number | null; label?: string; text?: string } | null
    note?: string | null
    basis?: string | null
    windows_total?: number
  } | null
  problems?: { title: string; count: number; names: string[] } | null
  /** Titled grids from producers without a lead (docs/findings-everywhere.md).
   *  Absent on strip producers; `[]` is legal (a pill-only minimal card). */
  tables?: FindingsTable[] | null
  [key: string]: unknown
}

/** Shape-sniff only — a findings block is anything with a verdict or a ref. */
export function parseFindingsBlock(raw: string): FindingsBlock | null {
  try {
    const data = JSON.parse(raw)
    if (
      data && typeof data === 'object' &&
      ((data.verdict && typeof data.verdict === 'object') || typeof data.ref === 'string')
    ) return data as FindingsBlock
  } catch {
    // Incomplete JSON while streaming, or malformed — the component falls back.
  }
  return null
}

export interface FindingsView {
  verdict: { key: FindingsVerdictKey; title: string; detail: string; level: SeverityKey; state: FindingsVerdictState | null }
  checked: number
  host: string
  caption: string | null
  /** The lead metric family read as one signal (peak, worst session, average). */
  leadDetail: string | null
  pattern: FindingsPattern | null
  /** null when the producer never measured movers — section omitted. */
  movers: NonNullable<FindingsBlock['movers']> | null
  normal: NonNullable<FindingsBlock['normal']> | null
  slate: FindingsSlate | null
  look: { title: string; text: string } | null
  recurring: { title: string; lines: string[] } | null
  processes: FindingsProcesses | null
  problems: { title: string; count: number; names: string[] } | null
  /** Non-null only when the producer titled it (the alert card's "What
   *  would settle it" / "Worth checking next"). */
  next: { title: string; items: string[] } | null
  /** Readable grids, in producer order; malformed entries dropped, never the card. */
  tables: FindingsTable[]
}

export type FindingsResult =
  | { ok: true; view: FindingsView }
  | { ok: false; reason: 'findings not loaded' | 'malformed findings' }

const VERDICT_KEYS: FindingsVerdictKey[] = ['clean', 'moved', 'no_spike', 'chronic', 'critical']
const VERDICT_STATES: FindingsVerdictState[] = ['confirmed', 'probable', 'inconclusive']
const ROW_TONES: FindingsRowTone[] = ['warn', 'strong', 'muted']

/** Which shaped icon heads the verdict line. Clean/no-spike = the green
 *  Normal check; movers = the runbook's own severity (strong → High);
 *  chronic (saturated around the clock) is never a green tick; critical is
 *  the red Disaster octagon. */
export function verdictLevel(key: FindingsVerdictKey, movers: FindingsMover[]): SeverityKey {
  if (key === 'critical') return 5
  if (key === 'chronic') return 2
  if (key !== 'moved') return 'ok'
  return movers.some((m) => m.kind === 'deviating' && m.severity === 'strong') ? 4 : 2
}

export function normalizeFindings(data: FindingsBlock | null | undefined): FindingsResult {
  if (!data || typeof data !== 'object') return { ok: false, reason: 'malformed findings' }
  const verdict = data.verdict
  if (!verdict || typeof verdict !== 'object') {
    // A bare ref: the backend expands it before the answer is stored, so this
    // is a block ahead of its payload (streaming) or an expired one.
    return { ok: false, reason: typeof data.ref === 'string' ? 'findings not loaded' : 'malformed findings' }
  }
  const key = VERDICT_KEYS.includes(verdict.key as FindingsVerdictKey)
    ? (verdict.key as FindingsVerdictKey)
    : 'clean'
  // A producer that measured nothing (movers: null / normal: null) gets no
  // section; a malformed one (present but unreadable) gets an empty section,
  // so a broken payload still shows its verdict.
  const movers = data.movers === null
    ? null
    : data.movers && Array.isArray(data.movers.items)
      ? data.movers
      : { title: '', summary: '', strongest_correlation: null, link_threshold: 0.6, items: [] }
  const normal = data.normal === null
    ? null
    : data.normal && Array.isArray(data.normal.groups)
      ? data.normal
      : { title: '', summary: '', groups: [], gaps: { count: 0, labels: [] } }
  const pattern = data.pattern && Array.isArray(data.pattern.days) && data.pattern.days.length
    ? data.pattern
    : null
  const slate = data.slate && Array.isArray(data.slate.items) && data.slate.items.length
    ? data.slate
    : null
  const state = VERDICT_STATES.includes(verdict.state as FindingsVerdictState)
    ? (verdict.state as FindingsVerdictState)
    : null
  return {
    ok: true,
    view: {
      verdict: {
        key,
        title: String(verdict.title ?? ''),
        detail: String(verdict.detail ?? ''),
        level: verdictLevel(key, movers?.items ?? []),
        state,
      },
      checked: typeof data.checked === 'number' ? data.checked : 0,
      host: String(data.subject?.host ?? ''),
      caption: typeof data.subject?.caption === 'string' && data.subject.caption ? data.subject.caption : null,
      leadDetail: typeof data.lead?.detail === 'string' && data.lead.detail ? data.lead.detail : null,
      pattern,
      movers,
      normal,
      slate,
      look: data.look && typeof data.look.text === 'string' ? data.look : null,
      recurring: titledLines(data.recurring),
      processes: processSection(data.processes),
      problems:
        data.problems && typeof data.problems.title === 'string' && Array.isArray(data.problems.names)
          ? { title: data.problems.title,
              count: typeof data.problems.count === 'number' ? data.problems.count : data.problems.names.length,
              names: data.problems.names.filter((n): n is string => typeof n === 'string' && !!n.trim()) }
          : null,
      next:
        data.next && typeof data.next.title === 'string' && data.next.title &&
        Array.isArray(data.next.ask_now) && data.next.ask_now.length
          ? { title: data.next.title,
              items: data.next.ask_now.filter((s): s is string => typeof s === 'string' && !!s.trim()) }
          : null,
      tables: tableSections(data.tables),
    },
  }
}

/** The readable grids of `tables`, in order. An entry needs a title, at
 *  least one column with a key, and a rows array; rows keep only string
 *  cells plus a known tone / indent, so a stray number or object never
 *  reaches the DOM. Malformed entries are dropped, never the card. */
function tableSections(raw: unknown): FindingsTable[] {
  if (!Array.isArray(raw)) return []
  const out: FindingsTable[] = []
  for (const t of raw) {
    if (!t || typeof t !== 'object') continue
    const tbl = t as Partial<FindingsTable>
    if (typeof tbl.title !== 'string' || !Array.isArray(tbl.columns) || !Array.isArray(tbl.rows)) continue
    const columns: FindingsTableColumn[] = []
    for (const c of tbl.columns) {
      if (!c || typeof c !== 'object' || typeof c.key !== 'string' || !c.key) continue
      columns.push({ key: c.key, label: typeof c.label === 'string' ? c.label : c.key,
                     align: c.align === 'right' ? 'right' : 'left' })
    }
    if (!columns.length) continue
    const rows: FindingsTableRow[] = []
    for (const r of tbl.rows) {
      if (!r || typeof r !== 'object') continue
      const row: FindingsTableRow = {}
      for (const c of columns) {
        const v = (r as Record<string, unknown>)[c.key]
        row[c.key] = typeof v === 'string' ? v : ''
      }
      const tone = (r as Record<string, unknown>).tone
      if (ROW_TONES.includes(tone as FindingsRowTone)) row.tone = tone as FindingsRowTone
      if ((r as Record<string, unknown>).indent === true) row.indent = true
      rows.push(row)
    }
    out.push({
      title: tbl.title, columns, rows,
      note: typeof tbl.note === 'string' && tbl.note ? tbl.note : null,
      empty: typeof tbl.empty === 'string' && tbl.empty ? tbl.empty : null,
    })
  }
  return out
}

/** A `{title, lines}` card section, or null when absent/empty/malformed. */
function titledLines(
  s: { title?: unknown; lines?: unknown } | null | undefined,
): { title: string; lines: string[] } | null {
  if (!s || typeof s.title !== 'string' || !s.title || !Array.isArray(s.lines)) return null
  const lines = s.lines.filter((l): l is string => typeof l === 'string' && !!l.trim())
  return lines.length ? { title: s.title, lines } : null
}

function isProcessRow(r: unknown): r is FindingsProcessRow {
  if (!r || typeof r !== 'object') return false
  const o = r as Record<string, unknown>
  return typeof o.name === 'string' && !!o.name && typeof o.range_text === 'string' &&
    typeof o.windows === 'number' && typeof o.windows_total === 'number'
}

/** The processes section: rows + peak when measured, lines otherwise, null
 *  when there is nothing readable to draw. */
function processSection(s: FindingsBlock['processes']): FindingsProcesses | null {
  if (!s || typeof s !== 'object' || typeof s.title !== 'string' || !s.title) return null
  const rows = Array.isArray(s.rows) ? s.rows.filter(isProcessRow) : []
  const lines = Array.isArray(s.lines)
    ? s.lines.filter((l): l is string => typeof l === 'string' && !!l.trim())
    : []
  const peak = s.peak && typeof s.peak === 'object' && typeof s.peak.text === 'string' && s.peak.text
    ? { at: typeof s.peak.at === 'number' ? s.peak.at : null,
        label: typeof s.peak.label === 'string' ? s.peak.label : '',
        text: s.peak.text }
    : null
  if (!rows.length && !lines.length && !peak) return null
  return {
    title: s.title,
    lines,
    rows,
    peak,
    note: typeof s.note === 'string' && s.note ? s.note : null,
    basis: typeof s.basis === 'string' ? s.basis : null,
    windowsTotal: typeof s.windows_total === 'number' ? s.windows_total : 0,
  }
}

/** Pill class for a slate outcome — refuted hypotheses read muted (ruled
 *  out), supported ones warn, undecided ones grey. */
export function slatePill(outcome: string): 'normal' | 'warn' | 'muted' {
  if (outcome === 'refutes') return 'normal'
  if (outcome === 'supports') return 'warn'
  return 'muted'
}

export function slateWord(outcome: string): string {
  if (outcome === 'refutes') return 'ruled out'
  if (outcome === 'supports') return 'supports'
  return 'undecided'
}

// --- day strip -----------------------------------------------------------------

/** What a day card shows: the green tick for a clean day (the whole card),
 *  a sparkline for a spike day that still has raw history, a peak-only note
 *  for a spike day where only trends survived, a dash for no data. */
export type DayKind = 'tick' | 'spark' | 'peak' | 'none'

export function dayKind(day: FindingsDay): DayKind {
  if (day.outcome === 'clean') return 'tick'
  if (day.outcome === 'spike') return day.points && day.points.length >= 2 ? 'spark' : 'peak'
  return 'none'
}

/** SVG path for a day sparkline in a `width`×`height` box; y from 0 to the
 *  larger of the day's own peak and the threshold, so a spike is tall on every
 *  card and a card never rescales the same threshold differently. */
export function sparklinePath(
  points: [number, number][],
  width: number,
  height: number,
  ceiling: number | null,
): string {
  if (points.length < 2) return ''
  const xs = points.map((p) => p[0])
  const ys = points.map((p) => p[1])
  const x0 = Math.min(...xs)
  const x1 = Math.max(...xs)
  const top = Math.max(ceiling ?? 0, ...ys, 1e-9)
  const pad = 1.5
  const w = width - pad * 2
  const h = height - pad * 2
  return points
    .map(([x, y], i) => {
      const px = pad + (x1 === x0 ? w / 2 : ((x - x0) / (x1 - x0)) * w)
      const py = pad + h - Math.max(0, Math.min(1, y / top)) * h
      return `${i === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`
    })
    .join(' ')
}

/** Threshold guide line y in the same box as `sparklinePath`. */
export function sparklineThresholdY(points: [number, number][], height: number, ceiling: number | null): number | null {
  if (ceiling == null || points.length < 2) return null
  const top = Math.max(ceiling, ...points.map((p) => p[1]), 1e-9)
  const pad = 1.5
  const h = height - pad * 2
  return pad + h - Math.max(0, Math.min(1, ceiling / top)) * h
}

// The strip's clock is the VIEWER's, like every chart axis in the product
// (Chart.js formats epochs in the browser zone). The server also stamps
// `label` / `spike_label` in its display timezone, but on the first real
// runs that resolved to UTC while the charts under it read local time —
// "spike 12:11" over an axis running 14:57–15:57. Same epoch, one clock.

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** `17:58` in the viewer's clock. */
export function clockLabel(epoch: number): string {
  const d = new Date(epoch * 1000)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

/** `Fri 8` in the viewer's clock — a day-strip card label. */
export function dayLabel(day: Pick<FindingsDay, 'start' | 'label'>): string {
  if (!Number.isFinite(day.start)) return day.label ?? ''
  const d = new Date(day.start * 1000)
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()}`
}

/** The small caption under a day card. */
export function dayCaption(day: FindingsDay): string {
  if (day.caption) return day.caption
  if (day.outcome === 'clean') return 'clean'
  if (day.outcome === 'no_data') return 'no data'
  if (day.spike_at != null && Number.isFinite(day.spike_at)) {
    return day.spike_at_precision === 'hour'
      ? `spike ~${clockLabel(day.spike_at)}`
      : `spike ${clockLabel(day.spike_at)}`
  }
  if (day.spike_label) return `spike ${day.spike_label}`
  return 'spike'
}

export function formatCorrelation(r: number | null | undefined): string {
  if (typeof r !== 'number' || Number.isNaN(r)) return '—'
  return `${r >= 0 ? '+' : '−'}${Math.abs(r).toFixed(2)}`
}
